/* eslint-disable react-refresh/only-export-components */
/**
 * PendingMessagesContext - Manages optimistic message sending
 *
 * Provides a queue-based system for sending messages with:
 * - Immediate UI feedback (optimistic updates)
 * - Background server synchronization
 * - Automatic retry with exponential backoff
 * - localStorage persistence for offline resilience
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import { useAuth } from './AuthContext';
import { sendMessage as dmSendMessage } from '@/services/dm';
import { sendLeadersChatMessage } from '@/services/leadersChat';
import { jitterMs } from '@/utils/random';
import { useMountedRef } from '@/hooks/useMountedRef';
import { useAuthCleanup } from './AuthCleanupContext';
import { uploadCommunityAttachment, uploadAttachment } from '@/services/storage';
import { trackMessageSent } from '@/services/analytics';
import { incrementMetric } from '@/services/engagementMetrics';
import { createReplyNotification } from '@/services/notifications';
import {
  MESSAGE_QUEUE_CONFIG,
  type PendingMessage,
  type OptimisticMessageInput,
  type ChatType,
} from '@/types/pendingMessage';
import type { Attachment } from '@/types/dm';
import {
  createPendingMessage,
  loadPersistedMessages,
  persistMessages,
  updateMessageStatus,
  removeMessage,
  addMessage,
  getMessagesForContext,
  shouldRetry,
  getRetryDelay,
  isOnline,
} from '@/services/messageQueue';

type SendMessageFn = (message: PendingMessage, attachments: Attachment[]) => Promise<string>;

async function sendMessageByChatType(
  message: PendingMessage,
  uploadedAttachments: Attachment[],
  chatType: ChatType,
  senders: { community: SendMessageFn; leaders: SendMessageFn; dm: SendMessageFn }
): Promise<string> {
  if (chatType === 'community') {
    return senders.community(message, uploadedAttachments);
  }
  if (chatType === 'leaders') {
    return senders.leaders(message, uploadedAttachments);
  }
  return senders.dm(message, uploadedAttachments);
}

function canProcessMessage(message: PendingMessage): boolean {
  return (
    message.status !== 'sent' && message.status !== 'uploading' && message.status !== 'sending'
  );
}

function markSentAndTrackAnalytics(
  message: PendingMessage,
  setPendingMessages: React.Dispatch<React.SetStateAction<PendingMessage[]>>,
  clientId: string,
  serverId: string,
  updateMessageStatus: (
    prev: PendingMessage[],
    clientId: string,
    updates: Partial<PendingMessage>
  ) => PendingMessage[]
): void {
  setPendingMessages((prev) => updateMessageStatus(prev, clientId, { status: 'sent', serverId }));
  trackMessageSent();
  if (message.userId) {
    incrementMetric(message.userId, 'messagesThisDay').catch(console.error);
    incrementMetric(message.userId, 'messagesThisWeek').catch(console.error);
  }
}

async function resolveAttachments(
  message: PendingMessage,
  uploadAttachments: (
    msg: PendingMessage,
    onProgress: (attachmentId: string, progress: number) => void
  ) => Promise<Attachment[]>,
  onProgress: (attachmentId: string, progress: number) => void
): Promise<Attachment[]> {
  const base = message.uploadedAttachments ? [...message.uploadedAttachments] : [];
  const hasPending = message.pendingAttachments && message.pendingAttachments.length > 0;
  if (!hasPending) return base;
  const uploaded = await uploadAttachments(message, onProgress);
  return [...base, ...uploaded];
}

function handleSendError(
  error: unknown,
  message: PendingMessage,
  clientId: string,
  setPendingMessages: React.Dispatch<React.SetStateAction<PendingMessage[]>>,
  scheduleRetry: (clientId: string, retryCount: number) => void,
  updateMessageStatus: (
    prev: PendingMessage[],
    clientId: string,
    updates: Partial<PendingMessage>
  ) => PendingMessage[]
): void {
  const errorMessage = error instanceof Error ? error.message : 'Failed to send message';

  if (shouldRetry(message)) {
    setPendingMessages((prev) =>
      updateMessageStatus(prev, clientId, {
        status: 'pending',
        retryCount: message.retryCount + 1,
        error: `Retrying... (${message.retryCount + 1}/${MESSAGE_QUEUE_CONFIG.MAX_RETRIES})`,
      })
    );
    scheduleRetry(clientId, message.retryCount + 1);
  } else {
    setPendingMessages((prev) =>
      updateMessageStatus(prev, clientId, {
        status: 'failed',
        error: errorMessage,
      })
    );
  }
}

/** Pure helper: update attachment progress for a message in the list */
function updateMessageAttachmentProgress(
  prev: PendingMessage[],
  clientId: string,
  attachmentId: string,
  progress: number
): PendingMessage[] {
  return prev.map((m) => {
    if (m.clientId !== clientId) return m;
    return {
      ...m,
      pendingAttachments: m.pendingAttachments?.map((a) =>
        a.id === attachmentId ? { ...a, uploadProgress: progress } : a
      ),
    };
  });
}

interface PendingMessagesContextValue {
  /** All pending messages in the queue */
  pendingMessages: PendingMessage[];

  /** Send a message optimistically (appears immediately, syncs in background) */
  sendOptimistically: (
    chatType: ChatType,
    input: OptimisticMessageInput,
    conversationId?: string
  ) => Promise<void>;

  /** Retry a failed message */
  retryMessage: (clientId: string) => void;

  /** Cancel/remove a pending or failed message */
  cancelMessage: (clientId: string) => void;

  /** Get pending messages for community chat */
  getCommunityPendingMessages: () => PendingMessage[];

  /** Get pending messages for a specific DM conversation */
  getDMPendingMessages: (conversationId: string) => PendingMessage[];

  /** Get pending messages for leaders chat */
  getLeadersPendingMessages: () => PendingMessage[];

  /** Check if a server message ID exists in pending (for deduplication) */
  hasPendingWithServerId: (serverId: string) => boolean;

  /** Clear all pending messages (for debugging/recovery) */
  clearAllPending: () => void;
}

const PendingMessagesContext = createContext<PendingMessagesContextValue | null>(null);

interface PendingMessagesProviderProps {
  children: ReactNode;
}

export function PendingMessagesProvider({ children }: PendingMessagesProviderProps) {
  const { authState } = useAuth();
  const { registerCleanup } = useAuthCleanup();
  const isMountedRef = useMountedRef();
  const [pendingMessages, setPendingMessagesState] = useState<PendingMessage[]>([]);

  // Ref to always access the latest pending messages (avoids stale closures)
  const pendingMessagesRef = useRef<PendingMessage[]>(pendingMessages);

  // Wrapper that updates both state and ref synchronously
  const setPendingMessages = useCallback(
    (updater: PendingMessage[] | ((prev: PendingMessage[]) => PendingMessage[])) => {
      setPendingMessagesState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        pendingMessagesRef.current = next; // Sync ref immediately
        return next;
      });
    },
    []
  );

  // Extract user and profile from authState (only available when authenticated)
  const user = authState.status === 'authenticated' ? authState.user : null;
  const profile = authState.status === 'authenticated' ? authState.profile : null;

  // Track retry timeouts to clean up on unmount
  const retryTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  // Track cleanup timeout
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track messages currently being processed to prevent double-processing
  const processingRef = useRef<Set<string>>(new Set());
  // Refs to avoid circular dependencies between useCallback hooks
  const scheduleRetryRef = useRef<((clientId: string, retryCount: number) => void) | null>(null);
  const processMessageRef = useRef<((clientId: string) => Promise<void>) | null>(null);

  // Register auth cleanup to clear pending messages on logout
  useEffect(() => {
    registerCleanup('pending-messages', () => {
      setPendingMessages([]);
      localStorage.removeItem(MESSAGE_QUEUE_CONFIG.STORAGE_KEY);
      retryTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      retryTimeoutsRef.current.clear();
      processingRef.current.clear();
    });
  }, [registerCleanup, setPendingMessages]);

  // Load persisted messages on mount
  useEffect(() => {
    const persisted = loadPersistedMessages();

    if (persisted.length > 0) {
      const now = Date.now();
      const STUCK_THRESHOLD_MS = 30000; // 30 seconds

      // Filter out stuck messages during load
      const validMessages = persisted.filter((msg) => {
        const ageMs = now - msg.createdAt;
        // Remove messages older than 30 seconds (they're stale from a previous session)
        if (ageMs > STUCK_THRESHOLD_MS) {
          return false;
        }
        // Remove sent messages (they should have been cleaned up)
        if (msg.status === 'sent') {
          return false;
        }
        return true;
      });

      if (validMessages.length > 0) {
        setPendingMessages(validMessages);
        // Process any pending messages that were interrupted
        // Use setTimeout to ensure scheduleRetry ref is initialized
        setTimeout(() => {
          validMessages.forEach((msg) => {
            if (msg.status === 'pending' || msg.status === 'failed') {
              // Schedule retry for interrupted messages
              scheduleRetryRef.current?.(msg.clientId, 0);
            }
          });
        }, 0);
      } else {
        // Clear localStorage if no valid messages remain
        localStorage.removeItem(MESSAGE_QUEUE_CONFIG.STORAGE_KEY);
      }
    }
  }, [setPendingMessages]);

  // Persist messages whenever they change
  useEffect(() => {
    persistMessages(pendingMessages);
  }, [pendingMessages]);

  // Clean up retry timeouts and processing locks for given client IDs
  const clearRetryAndProcessingFor = useCallback((clientIds: string[]) => {
    clientIds.forEach((clientId) => {
      const timeout = retryTimeoutsRef.current.get(clientId);
      if (timeout) {
        clearTimeout(timeout);
        retryTimeoutsRef.current.delete(clientId);
      }
      processingRef.current.delete(clientId);
    });
  }, []);

  // Cleanup sent messages and stuck 'sending' messages periodically
  useEffect(() => {
    const cleanup = () => {
      const now = Date.now();
      const STUCK_THRESHOLD_MS = 30000; // 30 seconds
      const toRemove: string[] = [];

      const filterStaleOrStuck = (m: PendingMessage) => {
        // Remove sent messages older than 5 seconds
        if (m.status === 'sent' && m.createdAt < now - 5000) {
          toRemove.push(m.clientId);
          return false;
        }
        // Remove stuck 'sending' or 'uploading' messages if status hasn't changed recently
        if (
          (m.status === 'sending' || m.status === 'uploading') &&
          (m.statusChangedAt ?? m.createdAt) < now - STUCK_THRESHOLD_MS
        ) {
          toRemove.push(m.clientId);
          return false;
        }
        return true;
      };

      setPendingMessages((prev) => {
        const filtered = prev.filter(filterStaleOrStuck);
        clearRetryAndProcessingFor(toRemove);
        return filtered;
      });
    };

    // Run cleanup every 10 seconds (more aggressive)
    cleanupTimeoutRef.current = setInterval(cleanup, 10000);
    // Also run cleanup once on mount to clear any stuck messages
    cleanup();

    return () => {
      if (cleanupTimeoutRef.current) {
        clearInterval(cleanupTimeoutRef.current);
      }
    };
  }, [setPendingMessages, clearRetryAndProcessingFor]);

  // Cleanup retry timeouts on unmount
  useEffect(() => {
    const timeoutsMap = retryTimeoutsRef.current;
    return () => {
      timeoutsMap.forEach((timeout) => clearTimeout(timeout));
      timeoutsMap.clear();
    };
  }, []);

  // Track online-retry timeouts to clean up on unmount
  const onlineRetryTimeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());

  // Schedule a single message for online retry
  const scheduleOnlineRetry = useCallback((msg: PendingMessage) => {
    const delay = jitterMs(1000);
    const timeout = setTimeout(() => {
      onlineRetryTimeoutsRef.current.delete(timeout);
      scheduleRetryRef.current?.(msg.clientId, 0);
    }, delay);
    onlineRetryTimeoutsRef.current.add(timeout);
  }, []);

  // Listen for online event to retry failed messages
  // Uses ref to avoid recreating listener on every state change
  useEffect(() => {
    const handleOnline = () => {
      // Read from ref to get latest state
      const toRetry = pendingMessagesRef.current.filter(
        (m) => m.status === 'failed' && shouldRetry(m)
      );
      toRetry.forEach(scheduleOnlineRetry);
    };

    window.addEventListener('online', handleOnline);
    // Capture ref value for cleanup (React lint rule: refs may change by cleanup time)
    const timeoutsSet = onlineRetryTimeoutsRef.current;
    return () => {
      window.removeEventListener('online', handleOnline);
      // Clear all pending online-retry timeouts
      timeoutsSet.forEach((timeout) => clearTimeout(timeout));
      timeoutsSet.clear();
    };
  }, [scheduleOnlineRetry]);

  /**
   * Schedule a retry for a message
   * Note: Uses processMessageRef to avoid circular dependency
   */
  const scheduleRetry = useCallback((clientId: string, retryCount: number) => {
    // Clear any existing retry timeout
    const existingTimeout = retryTimeoutsRef.current.get(clientId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const delay = getRetryDelay(retryCount);
    const timeout = setTimeout(() => {
      retryTimeoutsRef.current.delete(clientId);
      // Use ref to get latest processMessage
      processMessageRef.current?.(clientId);
    }, delay);

    retryTimeoutsRef.current.set(clientId, timeout);
  }, []);

  // Keep ref updated
  scheduleRetryRef.current = scheduleRetry;

  /**
   * Upload attachments for a message
   */
  const uploadAttachments = async (
    message: PendingMessage,
    onProgress: (attachmentId: string, progress: number) => void
  ): Promise<Attachment[]> => {
    if (!message.pendingAttachments || message.pendingAttachments.length === 0) {
      return [];
    }

    // Upload all attachments in parallel for better performance
    const uploadPromises = message.pendingAttachments.map(async (pending) => {
      if (pending.uploaded) {
        // Already uploaded
        return pending.uploaded;
      }

      if (!pending.file) {
        // File was lost (e.g., page refresh)
        throw new Error('Attachment file was lost. Please re-select the file.');
      }

      // Report initial progress
      onProgress(pending.id, 0);

      let attachment: Attachment;
      if (message.chatType === 'community' || message.chatType === 'leaders') {
        // Community and Leaders chat use the same upload function
        attachment = await uploadCommunityAttachment(pending.file);
      } else {
        // DM uploads - conversationId is required for DM messages
        const conversationId = message.conversationId;
        if (!conversationId) {
          throw new Error('Conversation ID required for DM attachment upload');
        }
        attachment = await uploadAttachment(conversationId, pending.file);
      }

      // Report completion
      onProgress(pending.id, 100);
      return attachment;
    });

    return Promise.all(uploadPromises);
  };

  /**
   * Process a single message (upload attachments and send to server)
   */
  const processMessage = useCallback(
    async (clientId: string) => {
      // Acquire processing lock IMMEDIATELY to prevent race conditions
      // The check-then-add pattern is atomic since JS is single-threaded for sync code
      if (processingRef.current.has(clientId)) {
        return;
      }
      processingRef.current.add(clientId);

      try {
        // Get the current message state from ref (avoids stale closure)
        const message = pendingMessagesRef.current.find((m) => m.clientId === clientId);
        if (!message) {
          return;
        }

        if (!canProcessMessage(message)) return;

        // Check if online
        if (!isOnline()) {
          // Will retry when back online (via online event listener)
          return;
        }
        const hasPendingUploads =
          message.pendingAttachments && message.pendingAttachments.length > 0;

        if (!isMountedRef.current) return;
        setPendingMessages((prev) =>
          updateMessageStatus(prev, clientId, {
            status: hasPendingUploads ? 'uploading' : 'sending',
            error: undefined,
          })
        );

        const updateAttachmentProgress = (attachmentId: string, progress: number) => {
          if (!isMountedRef.current) return;
          setPendingMessages((prev) =>
            updateMessageAttachmentProgress(prev, clientId, attachmentId, progress)
          );
        };
        const attachments = await resolveAttachments(
          message,
          uploadAttachments,
          updateAttachmentProgress
        );

        if (hasPendingUploads && !isMountedRef.current) return;
        if (hasPendingUploads) {
          setPendingMessages((prev) => updateMessageStatus(prev, clientId, { status: 'sending' }));
        }

        const serverId = await sendMessageByChatType(message, attachments, message.chatType, {
          community: sendCommunityMessage,
          leaders: sendLeadersChatMsg,
          dm: sendDMMessage,
        });

        if (!isMountedRef.current) return;
        markSentAndTrackAnalytics(
          message,
          setPendingMessages,
          clientId,
          serverId,
          updateMessageStatus
        );
      } catch (error) {
        console.error('Error sending message:', error);
        if (!isMountedRef.current) return;

        const currentMessage = pendingMessagesRef.current.find((m) => m.clientId === clientId);
        if (currentMessage) {
          handleSendError(
            error,
            currentMessage,
            clientId,
            setPendingMessages,
            scheduleRetry,
            updateMessageStatus
          );
        }
      } finally {
        // Release processing lock
        processingRef.current.delete(clientId);
      }
    },
    [scheduleRetry, setPendingMessages, isMountedRef]
  );

  // Keep ref updated for use in scheduleRetry
  processMessageRef.current = processMessage;

  /**
   * Send a community chat message to Firestore
   */
  const sendCommunityMessage = async (
    message: PendingMessage,
    attachments: Attachment[]
  ): Promise<string> => {
    const messageData: Record<string, unknown> = {
      text: message.text,
      userId: message.userId,
      author: message.author,
      authorRole: message.authorRole || 'member',
      timestamp: serverTimestamp(),
      // Include clientId for correlation with pending messages
      clientId: message.clientId,
    };

    if (attachments.length > 0) {
      messageData.attachments = attachments;
    }

    if (message.quotedMessageId && message.quotedMessage) {
      messageData.quotedMessageId = message.quotedMessageId;
      messageData.quotedMessage = message.quotedMessage;
    }

    const messageRef = await addDoc(
      collection(db, 'artifacts', appId, 'public', 'data', 'messages'),
      messageData
    );

    // Create notification for quoted user
    if (message.quotedMessage && message.quotedMessage.authorId !== message.userId) {
      try {
        await createReplyNotification(
          message.quotedMessage.authorId,
          message.userId,
          message.author,
          message.quotedMessage.id,
          messageRef.id,
          message.text
        );
      } catch (notifyError) {
        console.error('[Notifications] Failed to create reply notification:', notifyError);
      }
    }

    return messageRef.id;
  };

  /**
   * Send a DM message using the DM service
   */
  const sendDMMessage = async (
    message: PendingMessage,
    attachments: Attachment[]
  ): Promise<string> => {
    if (!message.conversationId) {
      throw new Error('Conversation ID required for DM');
    }

    const messagePayload = {
      text: message.text,
      attachments: attachments.length > 0 ? attachments : undefined,
      quotedMessageId: message.quotedMessageId,
      quotedMessage: message.quotedMessage,
      // Include clientId for correlating optimistic updates with server messages
      clientId: message.clientId,
    };

    return await dmSendMessage(
      message.conversationId,
      message.userId,
      messagePayload,
      message.author
    );
  };

  /**
   * Send a Leaders Chat message using the leaders chat service
   */
  const sendLeadersChatMsg = async (
    message: PendingMessage,
    attachments: Attachment[]
  ): Promise<string> => {
    const messagePayload = {
      text: message.text,
      attachments: attachments.length > 0 ? attachments : undefined,
      quotedMessageId: message.quotedMessageId,
      quotedMessage: message.quotedMessage,
      clientId: message.clientId,
      // Pass mention data through to the service
      mentions: message.mentions,
      mentionedUserIds: message.mentionedUserIds,
    };

    return await sendLeadersChatMessage(
      message.userId,
      message.author,
      message.authorRole || 'leader',
      messagePayload
    );
  };

  /**
   * Send a message optimistically
   */
  const sendOptimistically = useCallback(
    async (
      chatType: ChatType,
      input: OptimisticMessageInput,
      conversationId?: string
    ): Promise<void> => {
      if (!user || !profile) {
        throw new Error('User not authenticated');
      }

      // Validate input - check both files (community chat) and uploadedAttachments (DM)
      if (
        !input.text.trim() &&
        (!input.files || input.files.length === 0) &&
        (!input.uploadedAttachments || input.uploadedAttachments.length === 0)
      ) {
        return;
      }

      // Create pending message
      const pendingMessage = createPendingMessage(
        input,
        chatType,
        user.uid,
        profile.displayName || 'Member',
        profile.role,
        conversationId
      );

      // Add to queue (appears immediately in UI)
      setPendingMessages((prev) => addMessage(prev, pendingMessage));

      // Start processing (async)
      setTimeout(() => {
        processMessage(pendingMessage.clientId);
      }, 0);
    },
    [user, profile, processMessage, setPendingMessages]
  );

  /**
   * Retry a failed message
   */
  const retryMessage = useCallback(
    (clientId: string) => {
      const message = pendingMessagesRef.current.find((m) => m.clientId === clientId);
      if (!message || message.status !== 'failed') return;

      // Reset retry count and mark as pending
      setPendingMessages((prev) =>
        updateMessageStatus(prev, clientId, {
          status: 'pending',
          retryCount: 0,
          error: undefined,
        })
      );

      // Start processing after state update
      setTimeout(() => {
        processMessage(clientId);
      }, 0);
    },
    [processMessage, setPendingMessages]
  );

  /**
   * Cancel/remove a pending or failed message
   */
  const cancelMessage = useCallback(
    (clientId: string) => {
      // Clear any retry timeout
      const timeout = retryTimeoutsRef.current.get(clientId);
      if (timeout) {
        clearTimeout(timeout);
        retryTimeoutsRef.current.delete(clientId);
      }

      setPendingMessages((prev) => removeMessage(prev, clientId));
    },
    [setPendingMessages]
  );

  /**
   * Get pending messages for community chat
   */
  const getCommunityPendingMessages = useCallback(() => {
    return getMessagesForContext(pendingMessages, 'community');
  }, [pendingMessages]);

  /**
   * Get pending messages for a specific DM conversation
   */
  const getDMPendingMessages = useCallback(
    (conversationId: string) => {
      return getMessagesForContext(pendingMessages, 'dm', conversationId);
    },
    [pendingMessages]
  );

  /**
   * Get pending messages for leaders chat
   */
  const getLeadersPendingMessages = useCallback(() => {
    return getMessagesForContext(pendingMessages, 'leaders');
  }, [pendingMessages]);

  /**
   * Check if a server message ID exists in pending messages
   */
  const hasPendingWithServerId = useCallback(
    (serverId: string) => {
      return pendingMessages.some((m) => m.serverId === serverId);
    },
    [pendingMessages]
  );

  /**
   * Clear all pending messages (for recovery from stuck state)
   */
  const clearAllPending = useCallback(() => {
    setPendingMessages([]);
    localStorage.removeItem(MESSAGE_QUEUE_CONFIG.STORAGE_KEY);
    // Clear any pending retry timeouts
    retryTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    retryTimeoutsRef.current.clear();
  }, [setPendingMessages]);

  const value = useMemo(
    () => ({
      pendingMessages,
      sendOptimistically,
      retryMessage,
      cancelMessage,
      getCommunityPendingMessages,
      getDMPendingMessages,
      getLeadersPendingMessages,
      hasPendingWithServerId,
      clearAllPending,
    }),
    [
      pendingMessages,
      sendOptimistically,
      retryMessage,
      cancelMessage,
      getCommunityPendingMessages,
      getDMPendingMessages,
      getLeadersPendingMessages,
      hasPendingWithServerId,
      clearAllPending,
    ]
  );

  return (
    <PendingMessagesContext.Provider value={value}>{children}</PendingMessagesContext.Provider>
  );
}

export function usePendingMessages(): PendingMessagesContextValue {
  const context = useContext(PendingMessagesContext);
  if (!context) {
    throw new Error('usePendingMessages must be used within a PendingMessagesProvider');
  }
  return context;
}
